import React, { useRef, useLayoutEffect, useCallback } from 'react';
import { X, MessageSquare } from 'lucide-react';
import { useChat } from '../../context/ChatContext';
import { MessageItem } from './MessageItem';
import { MessageComposer } from './MessageComposer';

export const ThreadDrawer: React.FC = () => {
  const { activeThread, threadReplies, closeThread, sendReply } = useChat();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isNearBottomRef = useRef<boolean>(true);
  const prevThreadIdRef = useRef<string | null>(null);
  const prevRepliesCountRef = useRef<number>(0);

  const handleScroll = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const threshold = 100;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    isNearBottomRef.current = distanceFromBottom <= threshold;
  }, []);

  const scrollToBottomInstant = useCallback(() => {
    const el = scrollContainerRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
      isNearBottomRef.current = true;
    }
  }, []);

  useLayoutEffect(() => {
    if (!activeThread) return;

    const isThreadSwitched = prevThreadIdRef.current !== activeThread.id;

    if (isThreadSwitched) {
      prevThreadIdRef.current = activeThread.id;
      prevRepliesCountRef.current = threadReplies.length;
      isNearBottomRef.current = true;
      scrollToBottomInstant();
      requestAnimationFrame(scrollToBottomInstant);
      return;
    }

    if (threadReplies.length > prevRepliesCountRef.current) {
      if (isNearBottomRef.current) {
        scrollToBottomInstant();
      }
    }
    prevRepliesCountRef.current = threadReplies.length;
  }, [activeThread?.id, threadReplies.length, scrollToBottomInstant]);

  if (!activeThread) return null;

  return (
    <aside className="w-96 border-l border-border bg-card flex flex-col h-full z-20 flex-shrink-0">
      {/* Thread Header */}
      <div className="h-14 px-4 border-b border-border flex items-center justify-between font-bold text-foreground">
        <div className="flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-primary" />
          <span>Thread</span>
        </div>
        <button
          onClick={closeThread}
          title="Close Thread"
          className="p-1 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Parent Message & Replies Stream */}
      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto p-2 space-y-3"
      >
        {/* Parent message */}
        <div className="border-b border-border/80 pb-3">
          <MessageItem message={activeThread} isThreadParent />
        </div>

        {/* Replies divider */}
        <div className="flex items-center gap-2 px-3 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
          <span>{threadReplies.length} {threadReplies.length === 1 ? 'Reply' : 'Replies'}</span>
          <div className="flex-1 h-px bg-border/80" />
        </div>

        {/* Replies list */}
        <div className="space-y-1">
          {threadReplies.map((reply) => (
            <MessageItem key={reply.id} message={reply} isThreadParent />
          ))}
        </div>
      </div>

      {/* Thread Composer */}
      <MessageComposer
        onSendMessage={sendReply}
        placeholder={`Reply to ${activeThread.sender.displayName}...`}
      />
    </aside>
  );
};
